import type { FC, PropsWithChildren } from 'react'
import { useState } from 'react'
import styled from '@emotion/styled'
import Link from 'next/link'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import Image from '@segment/matcha/components/Image'

import CustomerCardBase, * as CustomerCard from '@segment/matcha/recipes/CustomerCard'

import type { CustomerStory, useMoreCustomersQuery } from '@segment/api/routes/customers/queries'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import contentfulImageLoader from '@segment/utils/contentfulImageLoader/contentfulImageLoader'

import { usePageConfig } from '@segment/contexts/page'

interface MoreCustomersProps {
  entries: ReturnType<typeof useMoreCustomersQuery>[`data`]
}
const MoreCustomers: FC<PropsWithChildren<MoreCustomersProps>> = ({ entries = [] }) => {
  const pageConfig = usePageConfig()
  return (
    <Wrapper>
      <StyledColumnTrack
        overrides={{
          sm: 2,
        }}
      >
        {entries.map((entry) => (
          <Card
            slug={entry.fields.slug}
            logo={entry.fields.matchaLogo?.fields?.logoMono}
            img={entry?.fields?.featuredImage?.fields.file.url}
            logoAltText={`${entry.fields.slug}'s company logo`}
            key={entry.sys.id}
          />
        ))}
      </StyledColumnTrack>
      <Link href={getAbsoluteUrl(`/customers`, pageConfig)}>
        <ButtonText tag="span">View all customers</ButtonText>
      </Link>
    </Wrapper>
  )
}

export default MoreCustomers

interface CardProps {
  slug?: string
  img?: string
  logo?: NonNullable<CustomerStory[`logo`]>
  logoAltText?: string
}
const Card: FC<CardProps> = ({ slug, img, logo, logoAltText }) => {
  const pageConfig = usePageConfig()
  const [isHover, setIsHover] = useState(false)

  return (
    <Link key={slug} href={getAbsoluteUrl(`/customers/${slug}/`, pageConfig)}>
      <CustomerCardBase
        tag="span"
        img={img}
        hoverable={true}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <CustomerCard.Row>
          {logo && (
            <Logo
              loader={() =>
                contentfulImageLoader({
                  src: logo.fields.file.url,
                  width: Number(logo.fields.file.details.image?.width),
                })
              }
              width={logo.fields.file.details.image?.width}
              height={logo.fields.file.details.image?.height}
              src={logo.fields.file.url}
              alt={logoAltText || ``}
            />
          )}
        </CustomerCard.Row>
        <CustomerCard.Row>
          <ButtonText variant="mono" appearance="dark" hover={isHover} iconEnd={ChevronRightIcon}>
            Read their story
          </ButtonText>
        </CustomerCard.Row>
      </CustomerCardBase>
    </Link>
  )
}

const Wrapper = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  gap: 40px;
  max-width: 1280px;
  margin: 0 auto;
  padding: 80px 24px;
`

const StyledColumnTrack = styled(ColumnTrack)`
  gap: 16px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    gap: 32px;
  }
`

const Logo = styled(Image)`
  max-height: 24px;
  width: auto;
`
